/**
 * 84. Document Library — folder tree; document preview; version
 * history; approvals; upload.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDate, formatQuantity } from "@/lib/money";

interface DocumentRow {
  id: string;
  name: string;
  folder: string;
  mimeType: string;
  sizeBytes: number;
  uploadedBy: string;
  uploadedAt: string | null;
  version: number;
  status: "Active" | "PendingApproval" | "Superseded" | "Archived";
}

export function DocumentLibraryPage() {
  const { data, isLoading } = useApiList<DocumentRow>(
    ["documents"],
    "/documents",
  );

  return (
    <div>
      <PageHeader
        title="Document Library"
        subtitle="Folder tree, document preview, version history, and approvals."
        actions={<Button variant="primary">Upload document</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Documents (${data.total})`}>
          <DataTable<DocumentRow>
            rows={data.items}
            columns={[
              { key: "name", label: "Name" },
              { key: "folder", label: "Folder" },
              { key: "mimeType", label: "Type" },
              {
                key: "sizeBytes",
                label: "Size",
                numeric: true,
                render: (r) => `${formatQuantity(r.sizeBytes / 1024, 1)} KB`,
              },
              { key: "uploadedBy", label: "Uploaded by" },
              {
                key: "uploadedAt",
                label: "Uploaded",
                render: (r) => (r.uploadedAt ? formatDate(r.uploadedAt) : "—"),
              },
              {
                key: "version",
                label: "Version",
                numeric: true,
                render: (r) => `v${r.version}`,
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Active" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "PendingApproval" ? (
                    <Badge tone="warning">{r.status}</Badge>
                  ) : (
                    <Badge tone="default">{r.status}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
